import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Tabs,
  Tab,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { USER_TASK_STATUS_OPTIONS, getTaskStatusTitle, isTaskClosed } from 'modules/dashboard/taskStatus';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import ConfirmationDialog from 'modules/common/confirmationDialog.component';

// The modal's outcomes, passed back through onSubmit; each carries its comment subject prefix and success message
export const TASK_MODAL_ACTION = {
  ATTEMPT: { subject: 'Attempted', successMessage: 'Task attempt recorded' },
  COMPLETE: { subject: 'Completed', successMessage: 'Task marked as completed' },
  CANCEL: { subject: 'Canceled', successMessage: 'Task canceled' },
};

const useStyles = makeStyles((theme) => ({
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  statusSelect: {
    minWidth: 200,
  },
  taskInfo: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  infoRow: {
    marginBottom: theme.spacing(0.5),
  },
  tabContent: {
    minHeight: 300,
  },
  commentsTable: {
    maxHeight: 400,
    overflow: 'auto',
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.grey[100],
  },
  commentText: {
    whiteSpace: 'pre-wrap',
  },
  noComments: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  cancelTaskButton: {
    marginRight: 'auto',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

const TaskCallModal = ({ open, task, onClose, onSubmit, isLoading, comments }) => {

  const classes = useStyles();
  const [commentText, setCommentText] = useState('');
  const [isClientViewable, setIsClientViewable] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);
  const [selectedStatusId, setSelectedStatusId] = useState('');

  // Clear comment and reset to first tab when modal opens
  useEffect(() => {
    if (open) {
      setCommentText('');
      setIsClientViewable(false);
      setActiveTab(0);
      setIsCancelConfirmationOpen(false);
    }
  }, [open]);

  const currentStatusId = task ? task.taskStatusId : '';
  useEffect(() => {
    if (open) {
      setSelectedStatusId(currentStatusId || '');
    }
  }, [open, currentStatusId]);

  // A status picked here is held until Record Attempt saves it; Mark Completed and Cancel Task set their own status
  const handleAttempt = () => {
    onSubmit(TASK_MODAL_ACTION.ATTEMPT, commentText, isClientViewable, selectedStatusId);
  };

  const handleComplete = () => {
    onSubmit(TASK_MODAL_ACTION.COMPLETE, commentText, isClientViewable);
  };

  const handleConfirmCancel = () => {
    setIsCancelConfirmationOpen(false);
    onSubmit(TASK_MODAL_ACTION.CANCEL, commentText, isClientViewable);
  };

  const handleCommentChange = (e) => {
    setCommentText(e.target.value);
    if (e.target.value.length === 0) {
      setIsClientViewable(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (!task) {
    return null;
  }

  // A completed or canceled task is read-only: no status change, notes or actions
  const isClosed = isTaskClosed(task);
  // The selector shows any status but offers only the user-set ones; any other status shows as its label only
  const selectedUserStatus = USER_TASK_STATUS_OPTIONS.find(s => isIdEqual(s.id, selectedStatusId));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.titleBar}>
        <Typography variant="h6">{task.title || 'Task Action'}</Typography>
        <TextField
          select
          label="Status"
          size="small"
          variant="outlined"
          className={classes.statusSelect}
          value={selectedUserStatus ? selectedUserStatus.id : ''}
          onChange={(e) => setSelectedStatusId(e.target.value)}
          disabled={isLoading || isClosed}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true, renderValue: () => getTaskStatusTitle(selectedStatusId) }}
        >
          {USER_TASK_STATUS_OPTIONS.map((status) => (
            <MenuItem key={status.id} value={status.id}>{status.title}</MenuItem>
          ))}
        </TextField>
      </DialogTitle>
      <DialogContent dividers>
        <Box className={classes.taskInfo}>
          <Typography variant="body2" className={classes.infoRow}>
            <strong>File:</strong> {task.fileNumber || 'N/A'} - {task.eeFullName || 'N/A'}
          </Typography>
          <Typography variant="body2" className={classes.infoRow}>
            <strong>Client:</strong> {task.clientName || 'N/A'}
          </Typography>
          <Typography variant="body2" className={classes.infoRow}>
            <strong>Type:</strong> {task.taskTypeDescription || 'N/A'}
          </Typography>
          {task.vendorName && (
            <Typography variant="body2" className={classes.infoRow}>
              <strong>Vendor:</strong> {task.vendorName}
            </Typography>
          )}
        </Box>

        <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
          <Tab label="Add Comment" />
          <Tab label="View Comments" />
        </Tabs>

        <Divider />

        <Box className={classes.tabContent} mt={2}>
          {activeTab === 0 && (
            <Box>
              <TextField
                label="Notes (optional)"
                multiline
                rows={6}
                fullWidth
                variant="outlined"
                value={commentText}
                onChange={handleCommentChange}
                placeholder="Enter notes about this attempt, completion or cancellation..."
                disabled={isLoading || isClosed}
              />
              {/* Notes are saved as an authorization comment, only when text is entered */}
              <FormControlLabel
                control={
                  <Switch
                    checked={isClientViewable}
                    onChange={(e) => setIsClientViewable(e.target.checked)}
                    color="primary"
                    disabled={isLoading || isClosed || !commentText.trim()}
                  />
                }
                label="Client viewable"
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              {comments && comments.length > 0 ? (
                <TableContainer className={classes.commentsTable}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.headerCell}>Date</TableCell>
                        <TableCell className={classes.headerCell}>Added By</TableCell>
                        <TableCell className={classes.headerCell}>Subject</TableCell>
                        <TableCell className={classes.headerCell}>Comment</TableCell>
                        <TableCell className={classes.headerCell}>Client Viewable</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comments.map((comment, index) => (
                        <TableRow key={comment.commentId || index}>
                          {/* addedOn carries a UTC offset, so this shows the viewer's local time */}
                          <TableCell>{comment.addedOn ? new Date(comment.addedOn).toLocaleString('en-US') : '-'}</TableCell>
                          <TableCell>{comment.addedByName || '-'}</TableCell>
                          <TableCell>{comment.subject}</TableCell>
                          <TableCell className={classes.commentText}>{comment.comment}</TableCell>
                          <TableCell>{comment.isClientViewable ? 'Yes' : 'No'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography className={classes.noComments}>
                  No comments yet for this task
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {activeTab === 0 && (
          <Button
            onClick={() => setIsCancelConfirmationOpen(true)}
            variant="contained"
            className={classes.cancelTaskButton}
            disabled={isLoading || isClosed}
          >
            Cancel Task
          </Button>
        )}
        <Button onClick={onClose} disabled={isLoading}>
          Close
        </Button>
        {activeTab === 0 && (
          <>
            <Button
              onClick={handleAttempt}
              color="primary"
              disabled={isLoading || isClosed}
            >
              Record Attempt
            </Button>
            <Button
              onClick={handleComplete}
              color="primary"
              variant="contained"
              disabled={isLoading || isClosed}
            >
              Mark Completed
            </Button>
          </>
        )}
      </DialogActions>
      <ConfirmationDialog
        open={isCancelConfirmationOpen}
        title="Cancel Task?"
        confirmLabel="Cancel Task"
        cancelLabel="Dismiss"
        onConfirm={handleConfirmCancel}
        onCancel={() => setIsCancelConfirmationOpen(false)}
      >
        <Typography>
          Are you sure you want to cancel this task? {commentText.trim() ? 'Your note will be saved with it.' : ''}
        </Typography>
      </ConfirmationDialog>
    </Dialog>
  );
};

TaskCallModal.propTypes = {
  open: PropTypes.bool.isRequired,
  task: PropTypes.shape({
    taskId: PropTypes.number,
    taskStatusId: PropTypes.string,
    title: PropTypes.string,
    fileNumber: PropTypes.string,
    eeFullName: PropTypes.string,
    clientName: PropTypes.string,
    taskTypeDescription: PropTypes.string,
    vendorName: PropTypes.string,
    fileId: PropTypes.number,
    serviceId: PropTypes.number,
    regardingId: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  comments: PropTypes.arrayOf(PropTypes.shape({
    commentId: PropTypes.number,
    subject: PropTypes.string,
    comment: PropTypes.string,
    addedByName: PropTypes.string,
    addedOn: PropTypes.string,
    isClientViewable: PropTypes.bool,
  })),
};

TaskCallModal.defaultProps = {
  task: null,
  isLoading: false,
  comments: [],
};

export default TaskCallModal;
